/*
  ATD - Complementarios 1
  Slider horizontal de productos complementarios ("Complementa tu look").

  Web component <atd-complementary1>:
   - Slider con scroll-snap nativo (mobile-first). Sin librerías.
   - Flechas prev/next (escritorio) con LOOP: avanzan una tarjeta por click y
     hacen wrap-around (del final vuelven a la primera, y al revés). Dots
     clicables (patrón Katy: forma+color, área táctil >=44px, accesible).
   - Mobile: swipe nativo intacto (scroll-snap). El loop con flechas es un
     comportamiento de escritorio; en mobile el índice se deriva del scroll.
   - Por tarjeta: selector de variante que actualiza el precio del botón y la
     variante a agregar; variantes agotadas deshabilitadas.
   - "Agregar" = AJAX /cart/add.js + abre el cart drawer del tema (mismo patrón
     probado en atd-cart-drawer-extras.js, sin tocar core de Horizon).

  Navegación por PÁGINAS (no por tarjeta):
   - Una "página" es una posición de avance REAL del carril. Con V tarjetas
     visibles y N totales hay `paginas = max(1, N - V + 1)`: cada página deja
     una tarjeta nueva a la derecha hasta que la última quede pegada al borde.
   - Los dots se generan dinámicamente = nº de páginas (no nº de tarjetas).
     Esto evita los dots "muertos" del final donde el scroll ya topó pero el
     dot seguía marcándose. Se recalculan en resize (desktop<->mobile).
   - En mobile cabe ~1 tarjeta visible (con peek) → paginas ≈ N, un dot por
     tarjeta, como antes; el swipe nativo y el scroll-snap no se tocan.
*/
(() => {
  if (customElements.get('atd-complementary1')) return;

  const CART_DRAWER_SEL = 'cart-drawer-component';

  class AtdComplementary1 extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-atd-comp-track]');
      this.prevBtn = this.querySelector('[data-atd-comp-prev]');
      this.nextBtn = this.querySelector('[data-atd-comp-next]');
      this.dotsWrap = this.querySelector('[data-atd-comp-dots]');
      this.currency = this.dataset.currency || 'MXN';
      this.moneyFormat = this.dataset.moneyFormat || '';

      if (!this.track) return;

      this.#wireCards();
      this.#wireSlider();
    }

    /* ---------------- Tarjetas: variante + add to cart ---------------- */
    #wireCards() {
      this.querySelectorAll('[data-atd-comp-card]').forEach((card) => {
        const select = card.querySelector('[data-atd-comp-variant]');
        const addBtn = card.querySelector('[data-atd-comp-add]');
        if (!addBtn) return;

        if (select) {
          select.addEventListener('change', () => this.#syncCardFromVariant(card, select, addBtn));
          this.#syncCardFromVariant(card, select, addBtn);
        }

        addBtn.addEventListener('click', (e) => this.#handleAdd(e, card, addBtn));
      });
    }

    #syncCardFromVariant(card, select, addBtn) {
      const opt = select.options[select.selectedIndex];
      if (!opt) return;
      const variantId = opt.value;
      const available = opt.dataset.available === 'true';
      const priceCents = Number(opt.dataset.price || '0');

      addBtn.dataset.variantId = variantId;
      addBtn.disabled = !available;

      const priceEl = addBtn.querySelector('[data-atd-comp-price]');
      if (priceEl) priceEl.textContent = this.#formatMoney(priceCents);

      const labelEl = addBtn.querySelector('[data-atd-comp-add-label]');
      if (labelEl) {
        labelEl.textContent = available
          ? (addBtn.dataset.labelAdd || 'Agregar')
          : (addBtn.dataset.labelSoldout || 'Agotado');
      }
    }

    async #handleAdd(e, card, btn) {
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId || btn.disabled) return;

      btn.classList.add('is-loading');
      btn.disabled = true;

      try {
        const drawer = await this.#findDrawer();

        const cartItems =
          drawer?.querySelector('cart-items-component[data-section-id]') ||
          drawer?.querySelector('cart-items-component');
        const sectionId =
          cartItems?.dataset.sectionId ||
          cartItems?.getAttribute('data-section-id') ||
          cartItems?.getAttribute('section-id') ||
          null;

        const formData = new FormData();
        formData.set('id', variantId);
        formData.set('quantity', '1');
        if (sectionId) formData.set('sections', sectionId);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData,
          credentials: 'same-origin',
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          console.error('[atd-complementary1] add failed', errBody);
          this.#flashError(btn);
          return;
        }

        const data = await response.json();

        // Notificar a Horizon (refresca items del drawer vía Section Rendering)
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: 'atd-complementary1',
              data: {
                source: 'atd-complementary1',
                sections: data.sections || {},
                productId: card?.dataset.productId,
                variantId,
                itemCount: 1,
                didError: false,
              },
            },
          })
        );

        // Abrir el drawer
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else {
          // Fallback: si no hay drawer (cart page / drawer deshabilitado),
          // navegar al carrito para no dejar al usuario sin feedback.
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch (err) {
        console.error('[atd-complementary1] add error', err);
        this.#flashError(btn);
      } finally {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        // Re-sincronizar disabled por agotado tras quitar el loading
        const select = card.querySelector('[data-atd-comp-variant]');
        if (select) {
          const opt = select.options[select.selectedIndex];
          if (opt && opt.dataset.available !== 'true') btn.disabled = true;
        }
      }
    }

    #flashError(btn) {
      btn.classList.add('atd-complementary1__add--error');
      setTimeout(() => btn.classList.remove('atd-complementary1__add--error'), 1200);
    }

    async #findDrawer(maxWait = 3000) {
      const found = document.querySelector(CART_DRAWER_SEL);
      if (found) return found;
      return new Promise((resolve) => {
        const obs = new MutationObserver(() => {
          const node = document.querySelector(CART_DRAWER_SEL);
          if (node) {
            obs.disconnect();
            resolve(node);
          }
        });
        obs.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => {
          obs.disconnect();
          resolve(document.querySelector(CART_DRAWER_SEL));
        }, maxWait);
      });
    }

    /* ---------------- Slider: flechas con LOOP + dots por página + scroll-snap ----------------

       Modelo de navegación POR PÁGINA:
       - `this.index` es el índice lógico de la PÁGINA actual (0-based). Como
         avanzamos una tarjeta por página, la página i alinea la tarjeta i al
         borde izquierdo del carril.
       - `this.pages` = nº de páginas = max(1, totalTarjetas - visibles + 1).
         `visibles` se mide del carril real (clientWidth / ancho de tarjeta),
         así desktop y mobile dan el valor correcto sin hardcodear breakpoints.
       - Los dots se RECONSTRUYEN para que haya exactamente `this.pages` dots
         (no uno por tarjeta). Se recalcula en resize.
       - Flechas: avanzan/retroceden 1 página con WRAP módulo `pages`.
       - Dots: saltan a esa página (clamp simple, sin wrap).
       - Al navegar por flecha/dot hacemos scroll programático y marcamos el
         índice lógico nosotros. La última página deja la última tarjeta pegada
         al borde derecho (scrollLeft máximo), así el dot final SIEMPRE es un
         avance real — no quedan dots muertos.
       - En mobile el swipe nativo manda: el listener de scroll re-deriva la
         página desde scrollLeft (solo cuando el scroll NO fue programático),
         así el dot activo sigue al dedo sin que toquemos el snap táctil.
    -------------------------------------------------------------------------- */
    #wireSlider() {
      const cards = () => Array.from(this.track.querySelectorAll('[data-atd-comp-card]'));

      this.index = 0;
      this.pages = 1;
      // Bandera: ignorar el listener de scroll mientras corre una animación
      // programática (flecha/dot), para no pisar el índice lógico.
      this._programmatic = false;
      this._progTimer = null;

      const count = () => cards().length;

      const reducedMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const behavior = reducedMotion ? 'auto' : 'smooth';

      // Tarjetas visibles a la vez en el carril. Se deriva de medidas reales:
      // ancho útil del carril / (ancho de tarjeta + gap). round() tolera el
      // peek de mobile (una tarjeta + un cachito → cuenta como 1 visible).
      // Garantiza mínimo 1 para no terminar nunca con 0 páginas.
      const visibleCount = () => {
        const c = cards();
        if (c.length < 2) return Math.max(1, c.length);
        const cardW = c[0].getBoundingClientRect().width;
        const gap = Math.max(0, c[1].offsetLeft - (c[0].offsetLeft + c[0].offsetWidth));
        const per = cardW + gap;
        if (per <= 0) return 1;
        const v = Math.round(this.track.clientWidth / per);
        return Math.max(1, Math.min(v, c.length));
      };

      // Nº de páginas (posiciones de avance real). 6 tarjetas, 3 visibles → 4.
      const pageCount = () => {
        const n = count();
        if (n <= 0) return 1;
        return Math.max(1, n - visibleCount() + 1);
      };

      // Distancia de scroll para dejar la tarjeta de la página i alineada al
      // inicio del carril, clamp a los límites reales del scroll. La última
      // página topa con scrollWidth - clientWidth (última tarjeta a la derecha).
      const scrollLeftFor = (i) => {
        const c = cards();
        if (!c.length) return 0;
        const target = c[Math.max(0, Math.min(i, c.length - 1))];
        const raw = target.offsetLeft - this.track.offsetLeft;
        const max = this.track.scrollWidth - this.track.clientWidth;
        return Math.max(0, Math.min(raw, max));
      };

      // Deriva la página desde el scroll (mobile/swipe). Toma la tarjeta más
      // cercana al borde izquierdo y la clampa al rango de páginas válidas:
      // así un swipe hasta el tope cae en la última página, no en un índice
      // de tarjeta que no existe como página.
      const pageFromScroll = () => {
        const c = cards();
        if (!c.length) return 0;
        const left = this.track.scrollLeft + this.track.offsetLeft;
        let best = 0;
        let bestDist = Infinity;
        c.forEach((card, i) => {
          const dist = Math.abs(card.offsetLeft - left);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        });
        // Si el carril está al tope de scroll, forzamos la última página
        // (las últimas tarjetas comparten scrollLeft máximo en desktop).
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (max > 0 && this.track.scrollLeft >= max - 2) return this.pages - 1;
        return Math.max(0, Math.min(best, this.pages - 1));
      };

      // (Re)construye los dots para que haya exactamente `this.pages` botones.
      // Reusa el markup/clases del snippet Liquid (dot + dot-shape) para no
      // depender de CSS nuevo. Idempotente: si ya hay el nº correcto, solo
      // actualiza el estado activo.
      const buildDots = () => {
        if (!this.dotsWrap) return [];
        const want = this.pages;
        let existing = Array.from(this.dotsWrap.querySelectorAll('[data-atd-comp-dot]'));
        if (existing.length !== want) {
          this.dotsWrap.textContent = '';
          for (let i = 0; i < want; i += 1) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'atd-complementary1__dot';
            btn.setAttribute('data-atd-comp-dot', '');
            btn.setAttribute('role', 'tab');
            btn.setAttribute('aria-label', `Ir a la posición ${i + 1}`);
            const shape = document.createElement('span');
            shape.className = 'atd-complementary1__dot-shape';
            shape.setAttribute('aria-hidden', 'true');
            btn.appendChild(shape);
            this.dotsWrap.appendChild(btn);
          }
          existing = Array.from(this.dotsWrap.querySelectorAll('[data-atd-comp-dot]'));
          wireDots(existing);
        }
        return existing;
      };

      const renderDots = () => {
        const dots = this.dotsWrap
          ? Array.from(this.dotsWrap.querySelectorAll('[data-atd-comp-dot]'))
          : [];
        dots.forEach((d, i) => {
          const on = i === this.index;
          d.classList.toggle('is-active', on);
          if (on) {
            d.setAttribute('aria-current', 'true');
          } else {
            d.removeAttribute('aria-current');
          }
        });
      };

      // Marca un tramo de scroll como programático: durante ese tramo el
      // listener no re-deriva el índice. Se libera tras un margen de tiempo
      // suficiente para que termine el scroll suave.
      const lockProgrammatic = () => {
        this._programmatic = true;
        clearTimeout(this._progTimer);
        this._progTimer = setTimeout(() => {
          this._programmatic = false;
        }, reducedMotion ? 60 : 600);
      };

      // Navegación central. wrap=true permite el loop (flechas); wrap=false
      // hace clamp simple (dots / saltos directos). El módulo/clamp usan
      // `this.pages`, no el nº de tarjetas.
      const goTo = (i, { wrap = false } = {}) => {
        const p = this.pages;
        if (p <= 0) return;
        let target = i;
        if (wrap) {
          target = ((i % p) + p) % p; // módulo positivo sobre páginas
        } else {
          target = Math.max(0, Math.min(i, p - 1));
        }
        this.index = target;
        lockProgrammatic();
        this.track.scrollTo({ left: scrollLeftFor(target), behavior });
        renderDots();
      };

      // Cablea click + teclado en un set de dots. Se invoca cada vez que se
      // reconstruyen (los listeners viejos mueren con los nodos reemplazados).
      const wireDots = (dots) => {
        dots.forEach((dot, i) => {
          dot.addEventListener('click', () => goTo(i, { wrap: false }));
          dot.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
              e.preventDefault();
              goTo(this.index + 1, { wrap: true });
              const next = this.dotsWrap?.querySelectorAll('[data-atd-comp-dot]')[this.index];
              next?.focus();
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
              e.preventDefault();
              goTo(this.index - 1, { wrap: true });
              const prev = this.dotsWrap?.querySelectorAll('[data-atd-comp-dot]')[this.index];
              prev?.focus();
            }
          });
        });
      };

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => goTo(this.index - 1, { wrap: true }));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => goTo(this.index + 1, { wrap: true }));
      }

      // Recalcula nº de páginas + dots + estado estático. Se llama al inicio y
      // en cada resize. Si no hay overflow → 1 página y CSS oculta controles.
      const recompute = () => {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const overflowing = max > 2;
        this.classList.toggle('atd-complementary1--static', !overflowing);

        this.pages = overflowing ? pageCount() : 1;
        // Clamp del índice por si encogió el nº de páginas (desktop → mobile
        // amplía páginas; al revés las reduce).
        this.index = Math.max(0, Math.min(this.index, this.pages - 1));
        buildDots();
        renderDots();
      };

      // Listener de scroll: solo en scroll del usuario (swipe/drag/rueda)
      // re-derivamos la página. Durante scroll programático lo ignoramos.
      let raf = null;
      this.track.addEventListener('scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          if (this._programmatic) return;
          const idx = pageFromScroll();
          if (idx !== this.index) {
            this.index = idx;
            renderDots();
          }
        });
      });

      recompute();

      window.addEventListener('resize', () => {
        clearTimeout(this._rt);
        this._rt = setTimeout(() => {
          recompute();
          // Re-alinear el carril a la página lógica actual tras un resize
          // (cambia el ancho de tarjeta, el nº de páginas y el scroll máximo).
          this._programmatic = true;
          this.track.scrollTo({ left: scrollLeftFor(this.index), behavior: 'auto' });
          clearTimeout(this._progTimer);
          this._progTimer = setTimeout(() => {
            this._programmatic = false;
          }, 60);
          renderDots();
        }, 150);
      });
    }

    /* ---------------- Helpers ---------------- */
    #formatMoney(cents) {
      const value = cents / 100;
      try {
        return new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: this.currency || 'MXN',
          minimumFractionDigits: value % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        }).format(value);
      } catch (e) {
        return `$${value.toFixed(2)}`;
      }
    }
  }

  customElements.define('atd-complementary1', AtdComplementary1);
})();
